import { useEffect, useRef } from 'react'

type ModifierKey = 'ctrl' | 'alt' | 'shift' | 'meta'
type KeyCombo = string | ModifierKey

interface KeyboardShortcutOptions {
  /**
   * Whether to prevent default browser behavior
   * @default true
   */
  preventDefault?: boolean

  /**
   * Whether this shortcut should be disabled when user is typing in an input/textarea
   * @default true
   */
  ignoreInputs?: boolean

  /**
   * Whether this is a sequence (e.g., 'g' then 'd')
   * @default false
   */
  isSequence?: boolean
}

/**
 * Hook for registering keyboard shortcuts
 *
 * @example
 * // Simple key
 * useKeyboardShortcut(['ctrl', 'k'], () => openCommandPalette())
 *
 * // Sequence (press 'g' then 'd')
 * useKeyboardShortcut(['g', 'd'], () => navigate('/dashboard'), { isSequence: true })
 *
 * // Without preventing default
 * useKeyboardShortcut(['ctrl', 's'], () => saveForm(), { preventDefault: false })
 */
export function useKeyboardShortcut(
  keys: KeyCombo[],
  callback: (e?: KeyboardEvent) => void,
  options: KeyboardShortcutOptions = {}
) {
  const {
    preventDefault = true,
    ignoreInputs = true,
    isSequence = false,
  } = options

  const callbackRef = useRef(callback)
  const sequenceRef = useRef<string[]>([])
  const timeoutRef = useRef<NodeJS.Timeout>()

  // Keep callback fresh
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if user is typing in an input/textarea
      if (ignoreInputs) {
        const target = event.target as HTMLElement
        if (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        ) {
          return
        }
      }

      // Guard against undefined event.key (can happen with some input methods)
      if (!event.key) {
        return
      }

      // Handle sequences (e.g., 'g' then 'd')
      if (isSequence) {
        const key = event.key.toLowerCase()

        // Add key to sequence
        sequenceRef.current.push(key)

        // Clear sequence after 1 second of inactivity
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
        timeoutRef.current = setTimeout(() => {
          sequenceRef.current = []
        }, 1000)

        // Check if sequence matches
        const expectedSequence = keys.map((k) => k.toLowerCase())
        const actualSequence = sequenceRef.current.slice(-expectedSequence.length)

        if (JSON.stringify(actualSequence) === JSON.stringify(expectedSequence)) {
          if (preventDefault) {
            event.preventDefault()
          }
          sequenceRef.current = []
          callbackRef.current(event)
        }

        return
      }

      // Handle modifier + key combinations (e.g., Ctrl+K)
      const pressedModifiers = {
        ctrl: event.ctrlKey || event.metaKey, // metaKey = Cmd on Mac
        alt: event.altKey,
        shift: event.shiftKey,
        meta: event.metaKey,
      }

      const pressedKey = event.key.toLowerCase()

      // Extract expected modifiers and key from keys array
      const expectedModifiers: Set<ModifierKey> = new Set()
      let expectedKey = ''

      for (const keyCombo of keys) {
        if (['ctrl', 'alt', 'shift', 'meta'].includes(keyCombo)) {
          expectedModifiers.add(keyCombo as ModifierKey)
        } else {
          expectedKey = keyCombo.toLowerCase()
        }
      }

      // Check if all expected modifiers are pressed
      const modifiersMatch = Array.from(expectedModifiers).every(
        (mod) => pressedModifiers[mod]
      )

      // Check if no unexpected modifiers are pressed
      const noExtraModifiers = (['ctrl', 'alt', 'shift', 'meta'] as ModifierKey[]).every(
        (mod) => {
          if (expectedModifiers.has(mod)) {
            return true // Expected modifier
          }
          return !pressedModifiers[mod] // No extra modifiers
        }
      )

      // Check if key matches
      const keyMatches = pressedKey === expectedKey

      if (modifiersMatch && noExtraModifiers && keyMatches) {
        if (preventDefault) {
          event.preventDefault()
        }
        callbackRef.current(event)
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [keys, preventDefault, ignoreInputs, isSequence])
}
